import { AnimatedContentLoader } from '@components'
import { getSettingsPageUrl } from '@constants'
import { useBusinessContext } from '@context'
import { usePaygUsage } from '@query'
import { formatCurrency, formatDate, formatShortDate } from '@utils'
import { isLucaPaygUsageItem } from '@utils/lucaPaygBilling'
import { useLucaUsageVisibility } from '@utils/lucaUsageVisibility'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'
import { TrialUsageSection } from './TrialUsageSection'

export const PaygUsageSection: React.FC = () => {
  const { t } = useTranslation()
  const { slug, data } = useBusinessContext()
  const { showLucaUsage } = useLucaUsageVisibility()
  const isTrialing = data?.stripe_plan_status === 'trialing'
  const { data: usage, isLoading } = usePaygUsage(
    slug,
    !isTrialing && !!data?.stripe_subscription_id
  )
  const visibleItems = usage?.items ?? []
  const currency = usage?.currency ?? 'EUR'
  const subtotal = visibleItems.reduce((total, item) => total + item.amount, 0)
  const hasItems = visibleItems.length > 0

  const formatUnitPrice = (unitAmount: number | null, itemCurrency: string | null) => {
    if (unitAmount == null) {
      return '-'
    }

    const absAmount = Math.abs(unitAmount)
    const precision =
      absAmount > 0 && absAmount < 0.01
        ? { minimumFractionDigits: 2, maximumFractionDigits: 6 }
        : undefined

    return formatCurrency(unitAmount, precision, itemCurrency ?? currency)
  }

  if (isTrialing) {
    return <TrialUsageSection />
  }

  return (
    <AnimatedContentLoader isLoading={isLoading}>
      {usage?.period_start && usage.period_end ? (
        <SubscriptionPeriod>
          {t('settings.billing.paygUsage.subscriptionPeriod', {
            start: formatDate(usage.period_start),
            end: formatDate(usage.period_end)
          })}
        </SubscriptionPeriod>
      ) : null}
      <UsageTable role="table">
        <TableHead role="rowgroup">
          <HeaderRow role="row">
            <HeaderCell role="columnheader">
              {t('settings.billing.paygUsage.columns.product')}
            </HeaderCell>
            <HeaderCell $numeric role="columnheader">
              {t('settings.billing.paygUsage.columns.unitPrice')}
            </HeaderCell>
            <HeaderCell $numeric role="columnheader">
              {t('settings.billing.paygUsage.columns.quantity')}
            </HeaderCell>
            <HeaderCell $numeric role="columnheader">
              {t('settings.billing.paygUsage.columns.amount')}
            </HeaderCell>
          </HeaderRow>
        </TableHead>
        {hasItems ? (
          <>
            <TableBody role="rowgroup">
              {visibleItems.map(item => (
                <BodyRow
                  key={`${item.price_id}-${item.product_key ?? ''}-${item.period_start ?? ''}-${
                    item.period_end ?? ''
                  }`}
                  role="row"
                >
                  <ProductCell role="cell">
                    <ProductName>
                      {item.product_name}
                      {isLucaPaygUsageItem(item.product_key) && item.is_estimate ? (
                        <EstimateTag>{t('settings.billing.paygUsage.estimate')}</EstimateTag>
                      ) : null}
                    </ProductName>
                    {item.period_start && item.period_end ? (
                      <ProductPeriod>
                        {t('settings.billing.paygUsage.calendarPeriod', {
                          start: formatShortDate(item.period_start),
                          end: formatShortDate(item.period_end)
                        })}
                      </ProductPeriod>
                    ) : null}
                    {showLucaUsage && isLucaPaygUsageItem(item.product_key) ? (
                      <UsageLink
                        to={getSettingsPageUrl(slug, {
                          page: 'luca-usage'
                        })}
                      >
                        {t('settings.billing.paygUsage.realTimeUsageLink')}
                      </UsageLink>
                    ) : null}
                  </ProductCell>
                  <NumericCell role="cell">
                    {formatUnitPrice(item.unit_amount, item.currency)}
                  </NumericCell>
                  <NumericCell role="cell">{item.quantity}</NumericCell>
                  <NumericCell role="cell">
                    {formatCurrency(item.amount, null, item.currency ?? currency)}
                  </NumericCell>
                </BodyRow>
              ))}
            </TableBody>
            <TableFoot role="rowgroup">
              <FooterRow role="row">
                <FooterLabel role="cell">{t('settings.billing.paygUsage.subtotal')}</FooterLabel>
                <FooterAmount role="cell">{formatCurrency(subtotal, null, currency)}</FooterAmount>
              </FooterRow>
            </TableFoot>
          </>
        ) : (
          <EmptyRow role="row">
            <EmptyCell role="cell">{t('settings.billing.paygUsage.empty')}</EmptyCell>
          </EmptyRow>
        )}
      </UsageTable>
    </AnimatedContentLoader>
  )
}

const tableGrid = css`
  display: grid;
  grid-template-columns: minmax(0, 1fr) max-content max-content max-content;
  column-gap: ${({ theme }) => theme.spacing.md}rem;
  align-items: center;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const UsageTable = styled.div`
  ${tableGrid}
  width: 100%;
  max-width: 100%;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  overflow-x: auto;
  overflow-y: hidden;
`

const SubscriptionPeriod = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing.md}rem;
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
`

const TableHead = styled.div`
  display: contents;
`

const TableBody = styled.div`
  display: contents;
`

const TableFoot = styled.div`
  display: contents;
`

const HeaderRow = styled.div`
  display: contents;
`

const BodyRow = styled.div`
  display: contents;

  & > * {
    padding: ${({ theme }) => theme.spacing.sm}rem 0;
  }
`

const EmptyRow = styled.div`
  display: contents;

  & > * {
    padding: ${({ theme }) => theme.spacing.sm}rem 0;
  }
`

const FooterRow = styled.div`
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: subgrid;
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing.xs}rem;
  padding: ${({ theme }) => theme.spacing.sm}rem 0 0;
  border-top: 1px solid ${({ theme }) => theme.colors.neutralGray};
`

const HeaderCell = styled.span<{ $numeric?: boolean }>`
  padding-bottom: ${({ theme }) => theme.spacing.xs}rem;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.metalGray};
  text-align: ${({ $numeric }) => ($numeric ? 'right' : 'left')};
  white-space: nowrap;
  line-height: 1.25;
`

const ProductCell = styled.span`
  min-width: 0;
  white-space: normal;
  word-break: break-word;
`

const ProductName = styled.span`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  flex-wrap: wrap;
`

const EstimateTag = styled.span`
  padding: 0 ${({ theme }) => theme.spacing.xs}rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => `${theme.colors.primary}18`};
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 500;
`

const ProductPeriod = styled.span`
  display: block;
  margin-top: ${({ theme }) => theme.spacing.xxs}rem;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.metalGray};
`

const UsageLink = styled(Link)`
  display: block;
  margin-top: ${({ theme }) => theme.spacing.xs}rem;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  text-decoration: underline;
`

const NumericCell = styled.span`
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`

const EmptyCell = styled.span`
  grid-column: 1 / -1;
  color: ${({ theme }) => theme.colors.metalGray};
`

const FooterLabel = styled.span`
  grid-column: 1 / 4;
  text-align: right;
  font-weight: 400;
`

const FooterAmount = styled.span`
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`
